import {
  addDays,
  addMonths,
  addWeeks,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  parse,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns';

import type { DateRangePreset } from '@/types/schedule';

export interface DateRange {
  startDate: Date;
  endDate: Date;
  label: string;
}

/**
 * Get date range for a preset
 */
export function getDateRangeFromPreset(preset: DateRangePreset): DateRange {
  const now = new Date();

  switch (preset) {
    case 'lastMonth': {
      const lastMonth = subMonths(now, 1);
      return {
        startDate: startOfMonth(lastMonth),
        endDate: endOfMonth(lastMonth),
        label: 'Last Month',
      };
    }

    case 'lastWeek': {
      const lastWeek = subWeeks(now, 1);
      return {
        startDate: startOfWeek(lastWeek, { weekStartsOn: 0 }),
        endDate: endOfWeek(lastWeek, { weekStartsOn: 0 }),
        label: 'Last Week',
      };
    }

    case 'yesterday': {
      const yesterday = subDays(now, 1);
      return {
        startDate: startOfDay(yesterday),
        endDate: endOfDay(yesterday),
        label: 'Yesterday',
      };
    }

    case 'today':
      return {
        startDate: startOfDay(now),
        endDate: endOfDay(now),
        label: 'Today',
      };

    case 'tomorrow': {
      const tomorrow = addDays(now, 1);
      return {
        startDate: startOfDay(tomorrow),
        endDate: endOfDay(tomorrow),
        label: 'Tomorrow',
      };
    }

    case 'thisWeek':
      return {
        startDate: startOfWeek(now, { weekStartsOn: 0 }),
        endDate: endOfWeek(now, { weekStartsOn: 0 }),
        label: 'This Week',
      };

    case 'nextWeek': {
      const nextWeek = addWeeks(now, 1);
      return {
        startDate: startOfWeek(nextWeek, { weekStartsOn: 0 }),
        endDate: endOfWeek(nextWeek, { weekStartsOn: 0 }),
        label: 'Next Week',
      };
    }

    case 'thisMonth':
      return {
        startDate: startOfMonth(now),
        endDate: endOfMonth(now),
        label: 'This Month',
      };

    case 'nextMonth': {
      const nextMonth = addMonths(now, 1);
      return {
        startDate: startOfMonth(nextMonth),
        endDate: endOfMonth(nextMonth),
        label: 'Next Month',
      };
    }

    default:
      return {
        startDate: startOfDay(now),
        endDate: endOfDay(now),
        label: 'Today',
      };
  }
}

/** Past/current execution history — ES-backed rows only. */
export function getExecutionHistoryDateRangePresets(): Array<{
  value: DateRangePreset;
  label: string;
}> {
  return [
    { value: 'lastMonth', label: 'Last Month' },
    { value: 'lastWeek', label: 'Last Week' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'today', label: 'Today' },
    { value: 'thisWeek', label: 'This Week' },
  ];
}

/** Upcoming schedules — client-generated projection. */
export function getScheduledDateRangePresets(): Array<{
  value: DateRangePreset;
  label: string;
}> {
  return [
    { value: 'today', label: 'Today' },
    { value: 'tomorrow', label: 'Tomorrow' },
    { value: 'thisWeek', label: 'This Week' },
    { value: 'nextWeek', label: 'Next Week' },
    { value: 'thisMonth', label: 'This Month' },
    { value: 'nextMonth', label: 'Next Month' },
  ];
}

/**
 * Get all date range presets (history + scheduled).
 */
export function getAllDateRangePresets(): Array<{
  value: DateRangePreset;
  label: string;
}> {
  const seen = new Set<DateRangePreset>();
  const presets: Array<{ value: DateRangePreset; label: string }> = [];

  for (const preset of [
    ...getExecutionHistoryDateRangePresets(),
    ...getScheduledDateRangePresets(),
  ]) {
    if (seen.has(preset.value)) continue;
    seen.add(preset.value);
    presets.push(preset);
  }

  return presets;
}

/**
 * Format date range for API query
 */
export function formatDateForApi(date: Date): string {
  return format(date, "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
}

/**
 * Parse a date value that may arrive as an ISO string, the API's
 * "MM-DD-YYYY hh:mm A" format, or a Date object.
 */
export function toDate(date: Date | string): Date {
  if (date instanceof Date) return date;

  if (typeof date === 'string') {
    if (!date.trim()) return new Date(NaN);

    // API returns "MM-DD-YYYY hh:mm AM/PM" (e.g. "03-05-2026 5:15 PM")
    const apiMatch = date.match(/^(\d{2})-(\d{2})-(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (apiMatch) {
      const [, mm, dd, yyyy, hStr, min, ampm] = apiMatch;
      const year = parseInt(yyyy, 10);

      // Go's zero time (0001-01-01T00:00:00Z) means "no date" — treat as invalid
      if (year <= 1) return new Date(NaN);

      let hour = parseInt(hStr, 10);
      if (ampm.toUpperCase() === 'PM' && hour !== 12) hour += 12;
      if (ampm.toUpperCase() === 'AM' && hour === 12) hour = 0;
      return new Date(year, parseInt(mm, 10) - 1, parseInt(dd, 10), hour, parseInt(min, 10));
    }

    // Try date-fns parse for common API format as fallback
    const parsed = parse(date, 'MM-dd-yyyy h:mm a', new Date());
    if (!isNaN(parsed.getTime())) return parsed;

    // Fall back to native Date constructor (handles ISO 8601)
    return new Date(date);
  }

  return new Date(NaN);
}

/**
 * Format date for display in local timezone
 */
export function formatDateForDisplay(date: Date | string): string {
  const d = toDate(date);
  if (isNaN(d.getTime())) return '-';
  return format(d, 'MMM d, yyyy h:mm a');
}

/**
 * Format date for display in UTC
 */
export function formatDateForDisplayUTC(date: Date | string): string {
  const d = toDate(date);
  if (isNaN(d.getTime())) return '-';
  const year = d.getUTCFullYear();
  const month = d.getUTCMonth();
  const day = d.getUTCDate();
  const hours = d.getUTCHours();
  const minutes = d.getUTCMinutes();
  const utcAsLocal = new Date(year, month, day, hours, minutes);
  return `${format(utcAsLocal, 'MMM d, yyyy h:mm a')} UTC`;
}

/**
 * Format date only (no time)
 */
export function formatDateOnly(date: Date | string): string {
  const d = toDate(date);
  if (isNaN(d.getTime())) return '-';
  return format(d, 'MMM d, yyyy');
}

/**
 * Format time only
 */
export function formatTimeOnly(date: Date | string): string {
  const d = toDate(date);
  if (isNaN(d.getTime())) return '-';
  return format(d, 'h:mm a');
}

/**
 * Parse an HH:MM:SS or MM:SS duration string into total seconds.
 * Returns NaN if the format is unrecognised.
 */
function parseHmsDuration(value: string): number {
  const parts = value.split(':').map(Number);
  if (parts.some(isNaN)) return NaN;

  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }
  if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  }
  return NaN;
}

/**
 * Format duration to human-readable string.
 *
 * Accepts:
 *  - HH:MM:SS / MM:SS strings (from the trigger summary API)
 *  - Millisecond numbers
 */
export function formatDuration(durationMs: number | string | null | undefined): string {
  if (durationMs === null || durationMs === undefined || durationMs === '') {
    return '-';
  }

  let totalSeconds: number;

  if (typeof durationMs === 'string') {
    if (durationMs.includes(':')) {
      totalSeconds = parseHmsDuration(durationMs);
      if (isNaN(totalSeconds) || totalSeconds < 0) return '-';
    } else {
      const ms = parseFloat(durationMs);
      if (isNaN(ms) || !isFinite(ms) || ms < 0) return '-';
      if (ms === 0) return '-';
      totalSeconds = ms / 1000;
    }
  } else {
    if (isNaN(durationMs) || !isFinite(durationMs) || durationMs < 0) return '-';
    if (durationMs === 0) return '-';
    totalSeconds = durationMs / 1000;
  }

  if (totalSeconds <= 0) return '-';

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  if (minutes > 0) {
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  }
  return `${seconds}s`;
}

/**
 * Get relative time description (e.g., "2 hours ago", "in 3 days")
 */
export function getRelativeTimeDescription(date: Date | string): string {
  const d = toDate(date);
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  const diffSec = Math.abs(Math.floor(diffMs / 1000));
  const diffMin = Math.floor(diffSec / 60);
  const diffHours = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHours / 24);

  const isFuture = diffMs > 0;
  const prefix = isFuture ? 'in ' : '';
  const suffix = isFuture ? '' : ' ago';

  if (diffDays > 0) {
    return `${prefix}${diffDays} day${diffDays > 1 ? 's' : ''}${suffix}`;
  }
  if (diffHours > 0) {
    return `${prefix}${diffHours} hour${diffHours > 1 ? 's' : ''}${suffix}`;
  }
  if (diffMin > 0) {
    return `${prefix}${diffMin} minute${diffMin > 1 ? 's' : ''}${suffix}`;
  }
  return 'just now';
}
